import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/api';
import './AdminLogin.css';

const AdminLogin = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      const role = user.role;
      const adminRoles = ['main_admin', 'admin', 'invoicer', 'restaurant_owner', 'restaurant_user', 'store_owner', 'store_user', 'delivery_driver'];
      
      if (adminRoles.includes(role)) {
        if (role === 'main_admin' || role === 'admin') {
          navigate('/admin/main/dashboard');
        } else if (role === 'invoicer') {
          navigate('/admin/main/invoicing');
        } else if (role === 'restaurant_owner' || role === 'restaurant_user') {
          navigate('/admin/restaurant/dashboard');
        } else if (role === 'store_owner' || role === 'store_user') {
          navigate('/admin/store/dashboard');
        } else if (role === 'delivery_driver') {
          navigate('/admin/driver/dashboard');
        }
      }
    }
  }, [user, authLoading, navigate]);

  const getDashboardPath = (role) => {
    switch (role) {
      case 'main_admin': return '/admin/main/dashboard';
      case 'admin': return '/admin/main/dashboard';
      case 'invoicer': return '/admin/main/invoicing';
      case 'restaurant_owner':
      case 'restaurant_user': return '/admin/restaurant/dashboard';
      case 'store_owner':
      case 'store_user': return '/admin/store/dashboard';
      case 'delivery_driver': return '/admin/driver/dashboard';
      default: return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        const userRole = result.user?.role;

        // Check if user has admin access
        const adminRoles = ['main_admin', 'admin', 'invoicer', 'restaurant_owner', 'restaurant_user', 'store_owner', 'store_user', 'delivery_driver'];
        if (!adminRoles.includes(userRole)) {
          setError(t('admin.accessDenied') || 'Access denied. Admin access required.');
          logout();
          return;
        }
        // Defer navigation to next tick so AuthContext's setUser has committed
        // before the dashboard route mounts (AdminRoute must see the correct user).
        const path = getDashboardPath(userRole);
        if (path) {
          setTimeout(() => navigate(path), 0);
        }
      } else {
        setError(result.message || t('auth.loginError') || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || t('auth.loginError') || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-login-container">
      <Link to="/" className="admin-login-back">
        <i className="fas fa-arrow-left"></i> Back to site
      </Link>
      <div className="admin-login-box">
        <img
          src={`${process.env.PUBLIC_URL || ''}/images/logo.webp`}
          alt="Pussikuskit"
          className="admin-login-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <h1>Admin Login</h1>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <p className="admin-login-forgot">
            <Link to="/forgot-password">{t('auth.forgotPassword')}</Link>
          </p>
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
